import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Trophy, Crown, Award, Sparkles, ArrowLeft, Zap, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import api from "@/lib/api";

const TIER_ICON = {
  1: <Crown className="w-5 h-5 text-[#F4C75A]" strokeWidth={1.5} />,
  2: <Trophy className="w-5 h-5 text-[#C8B7A4]" strokeWidth={1.5} />,
  3: <Award className="w-5 h-5 text-[#D9A57B]" strokeWidth={1.5} />,
};

const TIER_BG = {
  1: "bg-[#1F1814] border-[#F4C75A]/40",
  2: "bg-[#1A1715] border-[#C8B7A4]/30",
  3: "bg-[#1A1715] border-[#D9A57B]/30",
};

export default function LeaderboardPage() {
  const { user, logout } = useAuth({ redirectIfUnauthed: false });
  const [data, setData] = useState(null);
  const [ladder, setLadder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    api.get("/leaderboard/ladder").then((r) => setLadder(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard/referrals?period=${period}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB]">
      <Helmet>
        <title>Affiliate Leaderboard · AgentReply Referral Rewards</title>
        <meta name="description" content="Top AgentReply affiliates ranked by paid signups. Win $200 cash or 12 months free Pro for #1. Plus 10% off renewals just for showing the badge." />
        <link rel="canonical" href="https://agentreply.io/leaderboard" />
      </Helmet>
      <Navbar user={user} onLogout={logout} />

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="leaderboard-page">
        <Link to="/dashboard" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] mb-10">
          <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to dashboard
        </Link>
        <header className="mb-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4 flex items-center gap-2">
            <Trophy className="w-3 h-3" strokeWidth={2} />Referral leaderboard
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mb-6 leading-[1.05]">
            Spread the word, win cash.
          </h1>
          <p className="font-mono text-base text-[#A39A8F] leading-relaxed max-w-3xl">
            Every "Powered by AgentReply" badge link is tracked. The two affiliates who drive the most paid signups each month win serious cash —
            and the top 10 join our Founding Affiliate programme for lifetime perks.
          </p>
        </header>

        {/* Prize tiles */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-testid="leaderboard-prizes">
          <div className={`${TIER_BG[1]} border p-6`}>
            <div className="flex items-center gap-2 mb-3">{TIER_ICON[1]}<span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4C75A]">#1 Champion</span></div>
            <div className="font-serif text-2xl text-[#F5F2EB] mb-2">$200 cash</div>
            <p className="font-mono text-xs text-[#A39A8F] leading-relaxed">
              Or 12 months free Pro — your pick. Awarded monthly to the affiliate with the most converted paid signups.
            </p>
          </div>
          <div className={`${TIER_BG[2]} border p-6`}>
            <div className="flex items-center gap-2 mb-3">{TIER_ICON[2]}<span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C8B7A4]">#2 Runner-up</span></div>
            <div className="font-serif text-2xl text-[#F5F2EB] mb-2">$100 cash</div>
            <p className="font-mono text-xs text-[#A39A8F] leading-relaxed">
              Or 6 months free Pro. Second place still gets bragging rights and a sweet bonus.
            </p>
          </div>
          <div className={`${TIER_BG[3]} border p-6`}>
            <div className="flex items-center gap-2 mb-3">{TIER_ICON[3]}<span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D9A57B]">Top 10 Founding</span></div>
            <div className="font-serif text-2xl text-[#F5F2EB] mb-2">Lifetime 20% off</div>
            <p className="font-mono text-xs text-[#A39A8F] leading-relaxed">
              Plus a Founding Affiliate badge on your profile, forever.
            </p>
          </div>
        </section>

        {/* Period switcher */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">Live rankings</div>
          <div className="flex gap-2" data-testid="leaderboard-period-switcher">
            {[
              { k: "all", l: "All time" },
              { k: "month", l: "This month" },
            ].map(({ k, l }) => (
              <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
                className={`font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 transition-colors ${period === k ? "bg-[#D95B38] text-[#F5F2EB]" : "border border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#D95B38]/40 hover:text-[#F5F2EB]"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Your rank tile */}
        {data?.me_rank && (
          <div className="bg-[#1A1715] border border-[#D95B38]/40 p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="leaderboard-me-rank">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] mb-1">Your rank</div>
              <div className="font-serif text-3xl text-[#F5F2EB]">#{data.me_rank.rank}</div>
            </div>
            <div className="font-mono text-xs text-[#A39A8F]">
              {data.me_rank.converted} converted · {data.me_rank.signups} signups
            </div>
          </div>
        )}

        {/* Leaderboard table */}
        {loading ? (
          <div className="flex items-center gap-3 text-[#A39A8F] font-mono text-sm">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading rankings…
          </div>
        ) : !data || data.leaderboard.length === 0 ? (
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-10 text-center font-mono text-sm text-[#A39A8F]" data-testid="leaderboard-empty">
            <Sparkles className="w-6 h-6 text-[#D95B38] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#F5F2EB] text-base mb-2">The leaderboard is wide open.</p>
            <p>Be the first by sharing your AgentReply widget — every click counts.</p>
          </div>
        ) : (
          <div className="bg-[#1A1715] border border-[#F5F2EB]/10 overflow-x-auto" data-testid="leaderboard-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#F5F2EB]/10">
                  <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">#</th>
                  <th className="text-left p-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Affiliate</th>
                  <th className="text-right p-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Converted</th>
                  <th className="text-right p-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Signups</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((row) => (
                  <tr key={row.rank}
                    className={`border-b border-[#F5F2EB]/5 last:border-0 ${row.is_me ? "bg-[#D95B38]/10" : ""}`}
                    data-testid={`leaderboard-row-${row.rank}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {row.rank <= 3 ? TIER_ICON[row.rank] : <span className="font-mono text-xs text-[#A39A8F] w-5">{row.rank}</span>}
                      </div>
                    </td>
                    <td className="p-4 font-serif text-base text-[#F5F2EB]">
                      {row.display_name}
                      {row.is_me && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38]">You</span>}
                    </td>
                    <td className="p-4 text-right font-mono text-sm text-[#D95B38] font-semibold">{row.converted}</td>
                    <td className="p-4 text-right font-mono text-sm text-[#A39A8F]">{row.signups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Milestone ladder (personal rewards) */}
        {ladder?.ladder && (
          <section className="mt-12 bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10" data-testid="public-milestone-ladder">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
              <Sparkles className="w-3 h-3" strokeWidth={2} /> Personal milestone rewards
            </div>
            <h2 className="font-serif text-3xl md:text-4xl mb-4 leading-[1.1]">
              You don't have to finish #1 to win.
            </h2>
            <p className="font-mono text-sm text-[#A39A8F] leading-relaxed mb-8 max-w-3xl">
              Every paid signup attributed to your widget unlocks a personal milestone code — single-use, locked to your account, applies at checkout.
              Hit the milestone, claim the reward. No quotas, no expiry pressure.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {ladder.ladder.map((m) => (
                <div key={m.id} className="bg-[#0F0D0C] border border-[#D95B38]/30 p-5" data-testid={`public-milestone-${m.id}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D95B38] mb-2">
                    {m.threshold} paid signup{m.threshold === 1 ? "" : "s"}
                  </div>
                  <div className="font-serif text-base text-[#F5F2EB] leading-snug">{m.headline}</div>
                </div>
              ))}
            </div>
            <p className="font-mono text-[11px] text-[#7A7068] mt-6">
              Codes are issued to your account automatically the moment a referred signup upgrades to paid. Check your AgentDetail → Share tab to apply.
            </p>
          </section>
        )}

        {/* How to climb */}
        <section className="mt-12 bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3">How to climb the ranks</div>
          <ol className="space-y-3 font-mono text-sm text-[#E0D8CA]">
            <li><span className="text-[#D95B38] font-semibold">01.</span> Turn the "Powered by AgentReply" badge ON in your <Link to="/dashboard" className="text-[#D95B38] underline">agent's Share tab</Link>.</li>
            <li><span className="text-[#D95B38] font-semibold">02.</span> Embed the widget on as many sites as you can — every visitor sees the badge.</li>
            <li><span className="text-[#D95B38] font-semibold">03.</span> Share your public chat link on social — `?ref=widget&u=...` is auto-appended.</li>
            <li><span className="text-[#D95B38] font-semibold">04.</span> When someone clicks → signs up → upgrades to paid, it counts towards your rank.</li>
          </ol>
        </section>
      </main>
      <Footer />
    </div>
  );
}
